import { Route, Routes, BrowserRouter, Outlet } from 'react-router-dom';
import Home from './pages';
import NotFound from './pages/NotFound';
import ProtectedRoute from './providers/protected-route';
import { AuthProvider } from './providers/auth-provider';
import SignUp from './pages/sign-up';
import {
  onlineManager,
  QueryClientProvider,
  useIsRestoring,
} from '@tanstack/react-query';
import { ForgotPassword } from './pages/forgot-password';
import { ResetPassword } from './pages/reset-password';
import { queryClient } from './providers/query-provider';
import Layout from './layouts/layout';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppointmentLoader, AppointmentPage } from './pages/appointment';
import OTPVerification from './pages/otp-verification';
import AcceptInvitationPage from './pages/accept';
import Profile from './pages/profile';
import { Login } from './pages/login-page';
import { fetchUserSettings } from './lib/utils';
import { StrictMode, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from './providers/theme-provider';
import i18n from './lib/internationalization';
import { UppyProvider } from './providers/uppy-provider';
import { ProjectLoader, ProjectPage } from '@/pages/project-page';
import { Toaster } from './components/ui/toaster';
import InstallPrompt from './components/install-prompt';
import UpdatePrompt from './components/update-prompt';
import ErrorBoundary from './components/error-boundary';

function App() {
  useEffect(() => {
    fetchUserSettings();
  }, []);

  const isRestoring = useIsRestoring();
  return (
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <ErrorBoundary>
          <ThemeProvider>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                }}
              >
                <AuthProvider>
                  <UppyProvider>
                    <InstallPrompt />
                    <UpdatePrompt />
                    <Toaster />
                    <Routes>
                      <Route path="/login" Component={Login}></Route>
                      <Route path="/signup" Component={SignUp}></Route>
                      <Route path="/forgot-password" Component={ForgotPassword}></Route>
                      <Route path="/reset-password" Component={ResetPassword}></Route>
                      <Route path="/verify" Component={OTPVerification} />
                      <Route path="/accept" Component={AcceptInvitationPage} />
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <Outlet />
                            </Layout>
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/" Component={Home} />
                        <Route path="/profile" Component={Profile} />
                        <Route
                          path="/project/:projectId"
                          loader={async ({ params }) =>
                            queryClient.getQueryData(['project', params.projectId]) ??
                            (onlineManager.isOnline() && !isRestoring
                              ? ProjectLoader(params.projectId!)
                              : undefined)
                          }
                          Component={ProjectPage}
                        />
                        <Route
                          path="/appointment/:appointmentId"
                          loader={async ({ params }) =>
                            queryClient.getQueryData([
                              'appointment',
                              params.appointmentId,
                            ]) ??
                            (onlineManager.isOnline() && !isRestoring
                              ? AppointmentLoader(params.appointmentId!)
                              : undefined)
                          }
                          Component={AppointmentPage}
                        />
                      </Route>
                      <Route path="*" Component={NotFound} />
                    </Routes>
                  </UppyProvider>
                </AuthProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </I18nextProvider>
    </StrictMode>
  );
}

export default App;
